/*
  Store Builder craft: the product page (16 Oct 2026). Contract: HOOKS.md (store-builder-build-2026-10-16) and
  store-kit/README.md, "Product page hooks". Hooks are data-sb-* on <html>, set before first paint by the patched
  layout/theme.liquid. No network, no markup from strings.
  - A gallery swapped by a colour pick: the phone's progress line at once (none for one photo), and data-sb-photos.
  - The title above the photos on a phone (--sb-phone-title). H4 delivery dates from the delivery line's own
    business-day range (--sb-delivery-dates), the kit's line under Add to Cart first (17 Oct).
  - 17 Oct 2026: H2 (the Frame's window-tall frame and its wheel) and H5 (the stack's rail) are gone: product photos
    are always a carousel with arrows (owner; house rules §5).
  - 18 Oct 2026: a tap on the product photo on a phone stays on the page (no zoom dialog; a computer still zooms).
  - 19 Oct 2026: a size chart shows inches or centimetres, switched above its table.
  - F2 (no size on arrival) is store-builder-craft-size.js.
*/
(() => {
const doc = document;
const root = doc.documentElement;
if (window.sbPdp) return;
window.sbPdp = true;

const hook = (name) => root.getAttribute(`data-sb-${name}`);
const all = (scope, selector) => [...scope.querySelectorAll(selector)];
const set = (el, name, value) => el.getAttribute(name) !== value && el.setAttribute(name, value);

// One pass per gallery, and again when a colour pick swaps it: the phone's progress line (store-builder-craft.js),
// drawn at once for the new gallery (none for one photo), and data-sb-photos, the photo count, on
// .product-information__media (the colour script sets it at the click).
function galleries() {
  for (const holder of all(doc, '.product-information__media')) {
    let seen = null;
    const update = () => {
      const gallery = holder.querySelector(':scope > media-gallery');
      if (!gallery || gallery === seen) return;
      const first = !seen;
      seen = gallery;
      const show = gallery.querySelector('slideshow-component');
      const n = all(gallery, 'slideshow-slide').length;
      set(holder, 'data-sb-photos', String(n));
      if (show && !first) {
        show.style.setProperty('--sb-n', String(n));
        show.toggleAttribute('data-sb-progress', n > 1);
      }
    };
    update();
    new MutationObserver(update).observe(holder, { childList: true });
  }
}

// The title and price above the photos on a phone; the originals stay for screen readers.
function heading() {
  if (hook('phone-title') !== 'above') return;
  for (const holder of all(doc, '.product-information__media')) {
    const details = holder.closest('.product-information')?.querySelector('.product-details');
    const title = details?.querySelector('h1');
    if (!title) continue;
    const box = Object.assign(doc.createElement('div'), { className: 'sb-phone-heading' });
    box.setAttribute('aria-hidden', 'true');
    holder.prepend(box);
    const copy = (el) => {
      el.setAttribute('data-sb-phone-copied', '');
      const made = el.localName.includes('-') ? Object.assign(doc.createElement('div'), { className: el.className }) : el.cloneNode(false);
      made.removeAttribute('data-sb-phone-copied');
      made.removeAttribute('id');
      made.append(...[...el.childNodes].map((node) => node.cloneNode(true)));
      return made;
    };
    const sync = () => {
      const price = details.querySelector('product-price');
      box.replaceChildren(...[title.closest('.text-block') || title, price].filter(Boolean).map(copy));
    };
    sync();
    let frame = 0;
    new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    }).observe(details, { subtree: true, childList: true, characterData: true });
  }
}

// H4: the Nth and Mth business day (Monday to Friday) after today, in the shopper's own calendar.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function businessDay(from, count) {
  const day = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  for (let left = count; left > 0;) {
    day.setDate(day.getDate() + 1);
    if (day.getDay() % 6 !== 0) left -= 1;
  }
  return day;
}

function arrives(today, first, last) {
  const [a, b] = [businessDay(today, first), businessDay(today, last)];
  const month = (d) => MONTHS[d.getMonth()];
  if (a.getMonth() === b.getMonth()) return `Arrives ${a.getDate()}–${b.getDate()} ${month(b)}`;
  return `Arrives ${a.getDate()} ${month(a)} – ${b.getDate()} ${month(b)}`;
}

const RANGE = /\b(\d{1,2}) to (\d{1,2}) business days\b/i;
function dates() {
  if (hook('delivery-dates') !== 'on') return;
  // 17 Oct 2026: the kit's own delivery line under Add to Cart first (snippets/sb-delivery-line.liquid).
  for (const block of [...all(doc, '.product-details .sb-delivery'), ...all(doc, '.product-details .text-block')]) {
    if (block.closest('details') || block.querySelector('.sb-arrives')) continue;
    const range = RANGE.exec(block.textContent || '');
    if (!range) continue;
    const [first, last] = [Number(range[1]), Number(range[2])];
    if (first < 1 || last < first) continue;
    const line = Object.assign(doc.createElement('p'), { className: 'sb-arrives', textContent: arrives(new Date(), first, last) });
    block.prepend(line);
    break;
  }
}

// 18 Oct 2026 (Archie, via the owner: the zoom "is clunky / hard for user to go back into product page"): on a phone a
// tap on the product photo stays on the page, and the shopper swipes or uses the arrows. Horizon opens its zoom dialog
// from the slide's on:click, read by one capture listener on the document; this one, on the window, runs first and
// stops the tap below 750px. A press that travelled is left alone (a drag is the slideshow's). A computer still zooms.
const PHONE = window.matchMedia?.('(max-width: 749px)');
const ZOOM = /^#zoom-dialog-[^/]+\/open\//;
function phoneTap() {
  let down = [0, 0];
  window.addEventListener('pointerdown', (event) => { down = [event.clientX, event.clientY]; }, { capture: true, passive: true });
  window.addEventListener('click', (event) => {
    const opener = PHONE?.matches && event.target instanceof Element ? event.target.closest('[on\\:click]') : null;
    if (!opener?.matches('media-gallery slideshow-slide') || !ZOOM.test(opener.getAttribute('on:click'))) return;
    if (Math.hypot(event.clientX - down[0], event.clientY - down[1]) > 10) return;
    event.stopPropagation();
  }, { capture: true });
}

// 19 Oct 2026 (Design 31, round 3: a 400px drawer cut off HIPS, a phone its cm column): each size chart's table shows
// one unit, picked by two buttons above it (aria-pressed): cm, unless the snippet's .sb-charts says data-sb-unit="in".
// Every chart on the page follows the last press. Without this script both units show and a wide table scrolls.
const UNITS = { in: ['Inches (in)', 'inches'], cm: ['Centimetres (cm)', 'centimetres'] };
let unitNow = null;
function showUnit(value) {
  unitNow = value;
  for (const charts of all(doc, '.sb-charts')) set(charts, 'data-sb-unit', value);
  for (const button of all(doc, '.sb-chart__unit')) set(button, 'aria-pressed', String(button.value === value));
  for (const cell of all(doc, '[data-sb-unit-cell]')) cell.hidden = cell.getAttribute('data-sb-unit-cell') !== value;
  for (const scroll of all(doc, '.sb-chart__scroll[data-sb-units]')) {
    set(scroll, 'aria-label', `${scroll.closest('.sb-chart')?.getAttribute('aria-label') ?? 'Size chart'}, in ${UNITS[value][1]}`);
  }
}

function units() {
  for (const table of all(doc, '.sb-chart__scroll:not([data-sb-units]) > .sb-chart__table')) {
    const [names, row] = table.tHead?.rows ?? [];
    const order = row ? [...row.cells].map((cell) => cell.textContent.trim().toLowerCase()) : [];
    if (!names || order.length === 0 || !order.every((name) => Object.hasOwn(UNITS, name))) continue;
    for (const line of table.tBodies[0]?.rows ?? []) {
      [...line.cells].filter((cell) => cell.localName === 'td').forEach((cell, i) => order[i] && cell.setAttribute('data-sb-unit-cell', order[i]));
    }
    for (const cell of names.cells) {
      cell.removeAttribute('colspan');
      cell.removeAttribute('rowspan');
      cell.scope = 'col';
    }
    row.hidden = true;
    const group = Object.assign(doc.createElement('div'), { className: 'sb-chart__units' });
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Units');
    for (const value of Object.keys(UNITS)) {
      const button = Object.assign(doc.createElement('button'), { type: 'button', className: 'sb-chart__unit', value, textContent: value });
      button.setAttribute('aria-label', UNITS[value][0]);
      group.append(button);
    }
    table.parentElement.setAttribute('data-sb-units', '');
    table.parentElement.before(group);
  }
  const asked = doc.querySelector('.sb-charts[data-sb-unit]')?.getAttribute('data-sb-unit');
  showUnit(unitNow ?? (asked === 'in' ? 'in' : 'cm'));
}
doc.addEventListener('click', ({ target }) => {
  const button = target instanceof Element ? target.closest('.sb-chart__unit') : null;
  if (button) showUnit(button.value);
  else if (target instanceof Element && target.closest('.popup-link__button, summary')) units(); // a re-rendered chart
});

galleries();
heading();
dates();
phoneTap();
units();
})();
